export const calculateGovernmentMilitary = (payload, taxInformationData) => {
  const nextPayload = { ...payload };

  const typeOfBenefit = nextPayload["Type of Benefit"];
  const grossMonthlyWages = parseFloat(nextPayload["Gross Monthly Wages"] || 0);
  const vaWaiver = parseFloat(nextPayload["VA Waiver / Offset"] || 0);
  const sbpPremium = parseFloat(nextPayload["Survivor Benefit Plan (SBP) Premium"] || 0);

  let amountSubjectToFederalTax = 0;
  let amountSubjectToStateTax = 0;
  let federalTaxationType = "Partially Taxable (Social Security Rules)";
  let stateTaxationType = "Ordinary Income";

  // 1. VA Disability (Tax-Free) Logic
  if (typeOfBenefit === "VA Disability (Tax-Free)") {
    amountSubjectToFederalTax = 0;
    amountSubjectToStateTax = 0;
    federalTaxationType = "Tax-Free (e.g., VA Disability)";
    stateTaxationType = "Tax Free";
  } 
  // 2. Military Pension / SBP Logic
  else if (typeOfBenefit === "Military Pension" || typeOfBenefit === "Survivor Benefit Plan (SBP)") {
    federalTaxationType = "Fully Taxable (Ordinary Income) (e.g., W-2, Traditional Pension)";
    stateTaxationType = "Ordinary Income";

    // Federal Formula: Gross Pay - SBP Premium - VA Waiver
    amountSubjectToFederalTax = Math.max(0, grossMonthlyWages - sbpPremium - vaWaiver);

    // State Exclusion Logic (2026 OBBBA Rules)
    const state = (taxInformationData && taxInformationData["State Taxation"]) || "";
    const clientAge = (taxInformationData && taxInformationData["Age"]) || 65; 
    const householdAGI = parseFloat((taxInformationData && taxInformationData["Adjusted Gross Income"]) || 0);

    let stateExclusion = 0;

    switch (state) {
      case "California":
      case "CA":
        // $20,000 Exclusion if AGI < $250k (MFJ) / $125k (Single)
        // Note: Using $250k as a general threshold for simulation
        if (householdAGI < 250000) {
          stateExclusion = 20000;
        }
        break;

      case "Colorado":
      case "CO":
        // $15,000 if under 55; $24,000 if 65 or older
        stateExclusion = clientAge >= 65 ? 24000 : 15000;
        break;

      case "Delaware":
      case "DE":
        stateExclusion = 12500;
        break;

      case "Georgia":
      case "GA":
        stateExclusion = 65000;
        break;

      case "Idaho":
      case "ID":
        // $41,140 if age 65+
        if (clientAge >= 65) {
          stateExclusion = 41140;
        }
        break;

      case "Kentucky":
      case "KY":
        stateExclusion = 31110;
        break;

      case "Maryland":
      case "MD":
        stateExclusion = 20000;
        break;

      case "New Mexico":
      case "NM":
        stateExclusion = 30000;
        break;

      case "Virginia":
      case "VA":
        stateExclusion = 40000;
        break;

      case "Vermont":
      case "VT":
        // 100% exempt if AGI < $125k; phases out between $125k–$175k
        if (householdAGI < 125000) {
          stateExclusion = amountSubjectToFederalTax;
        } else if (householdAGI < 175000) {
          const phaseOutRange = 50000; // 175 - 125
          const excess = householdAGI - 125000;
          const ratio = 1 - excess / phaseOutRange;
          stateExclusion = amountSubjectToFederalTax * ratio;
        }
        break;

      default:
        stateExclusion = 0;
        break;
    }

    // State Formula: Max(0, (Gross - SBP - VA) - State_Exclusion)
    // Note: State Exclusion is typically annual, dividing by 12 if wages are monthly
    const monthlyStateExclusion = stateExclusion / 12;
    amountSubjectToStateTax = Math.max(0, amountSubjectToFederalTax - monthlyStateExclusion);
  }

  nextPayload["Amount Subject to Federal Taxation"] = parseFloat(amountSubjectToFederalTax.toFixed(2));
  nextPayload["Amount Subject to State Taxation"] = parseFloat(amountSubjectToStateTax.toFixed(2));
  nextPayload["Federal Taxation Type"] = federalTaxationType;
  nextPayload["State Taxation Type"] = stateTaxationType;

  // Net Take-Home Formula
  // Net = Gross - SBP Premium - Estimated Taxes
  let fedTax = 0;
  let stateTax = 0;

  if (amountSubjectToFederalTax > 0 && taxInformationData) {
    const fedRate = parseFloat(taxInformationData["Federal Tax Rate"] || 0) / 100;
    const stateRate = parseFloat(taxInformationData["State Tax Effective Rate"] || 0) / 100;

    fedTax = amountSubjectToFederalTax * fedRate;
    stateTax = amountSubjectToStateTax * stateRate;
  }

  const netAmount = grossMonthlyWages - sbpPremium - (fedTax + stateTax);
  nextPayload["Net Amount"] = parseFloat(netAmount.toFixed(2));

  return nextPayload;
};

export const getGovernmentMilitaryConfig = (name, payload) => {
  const typeOfBenefit = payload && payload["Type of Benefit"];

  const config = {
    "Type of Benefit": { label: "Type of Benefit" },
    "Gross Monthly Wages": { label: "Gross Monthly Wages" },
    "Amount Subject to Federal Taxation": { readOnly: true },
    "Amount Subject to State Taxation": { readOnly: true },
    "Net Amount": { label: "Net Take-Home Pay", readOnly: true },
    "Provision": { label: "Pop-Up Provision" },
    "VA Waiver / Offset": { label: "VA Waiver / Offset" },
    "Survivor Benefit Plan (SBP) Premium": { label: "SBP Premium" },
  };

  // Logic to hide/remove fields based on selection
  if (typeOfBenefit === "Social Security") {
    const keepFields = [
      "Type of Benefit", 
      "Income Name", 
      "Owner", 
      "Date Income Starts", 
      "Date Income Ends", 
      "% Yearly Increase",
      "Income Source From Asset",
      "Notes",
      "Go to Social Security Module"
    ];
    if (!keepFields.includes(name)) {
      return { delete: true };
    }
    if (name === "Go to Social Security Module") {
      return { label: "Go to Social Security Module", isButton: true };
    }
  }

  if (typeOfBenefit === "VA Disability (Tax-Free)") {
    const hiddenFields = [
      "VA Waiver / Offset",
      "Survivor Benefit Plan (SBP) Premium",
      "Percent To Survivor",
      "Payee of Survivor Benefits",
      "Provision",
      "Survivor Name",
    ];
    if (hiddenFields.includes(name)) return { delete: true };
  }

  if (typeOfBenefit === "Military Pension") {
    const retireeFields = ["Percent To Survivor", "Payee of Survivor Benefits"];
    if (retireeFields.includes(name)) return { delete: true };
  }

  return config[name] || {};
};

export const getGovernmentMilitaryGroupPrefix = (groupName) => {
  if (groupName === "INCOME DETAILS") return "Part A: ";
  if (groupName === "ADDITIONAL DETAILS") return "Part B: ";
  if (groupName === "TAXATION") return "Part C: ";
  return "";
};
